/**
 * React hooks for GTD Project operations
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { GTDClient } from '../../../client';
import { 
  CreateProjectRequest,
  ProjectQueryFilters,
  ListQueryParams 
} from '../../../contracts';
import { useGTDClient } from '../provider';

// Query keys
const PROJECTS_QUERY_KEY = ['gtd', 'projects'] as const;

export function useProjects(filters?: ProjectQueryFilters & ListQueryParams) {
  const client = useGTDClient();
  
  return useQuery({
    queryKey: [...PROJECTS_QUERY_KEY, filters],
    queryFn: () => client.getProjects(filters),
    staleTime: 60 * 1000, // 1 minute
  });
}

export function useProject(id: string | null) {
  const client = useGTDClient();
  
  return useQuery({
    queryKey: [...PROJECTS_QUERY_KEY, id],
    queryFn: () => client.getProject(id!),
    enabled: !!id,
    staleTime: 60 * 1000,
  });
}

export function useCreateProject() {
  const client = useGTDClient();
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (request: CreateProjectRequest) => 
      client.createProject(request),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: PROJECTS_QUERY_KEY });
    },
  });
}

export function useCompleteProject() {
  const client = useGTDClient();
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ id, completedBy, notes }: { 
      id: string; 
      completedBy?: string; 
      notes?: string 
    }) => client.completeProject(id, completedBy, notes),
    onSuccess: (data, variables) => {
      queryClient.setQueryData([...PROJECTS_QUERY_KEY, variables.id], data);
      queryClient.invalidateQueries({ 
        queryKey: PROJECTS_QUERY_KEY,
        exact: false 
      });
    },
  });
}

export function useDeferProject() {
  const client = useGTDClient();
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (id: string) => client.deferProject(id),
    onSuccess: (data, id) => {
      queryClient.setQueryData([...PROJECTS_QUERY_KEY, id], data);
      queryClient.invalidateQueries({ 
        queryKey: PROJECTS_QUERY_KEY,
        exact: false 
      });
    },
  });
}

export function useActivateProject() {
  const client = useGTDClient();
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (id: string) => client.activateProject(id),
    onSuccess: (data, id) => {
      queryClient.setQueryData([...PROJECTS_QUERY_KEY, id], data);
      queryClient.invalidateQueries({ 
        queryKey: PROJECTS_QUERY_KEY,
        exact: false 
      });
    },
  });
}

export function useCancelProject() {
  const client = useGTDClient();
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (id: string) => client.cancelProject(id),
    onSuccess: (data, id) => {
      queryClient.setQueryData([...PROJECTS_QUERY_KEY, id], data);
      queryClient.invalidateQueries({ 
        queryKey: PROJECTS_QUERY_KEY,
        exact: false 
      });
    },
  });
}

export function useDeleteProject() {
  const client = useGTDClient();
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (id: string) => client.deleteProject(id),
    onSuccess: (_, id) => {
      queryClient.removeQueries({ queryKey: [...PROJECTS_QUERY_KEY, id] });
      queryClient.invalidateQueries({ 
        queryKey: PROJECTS_QUERY_KEY,
        exact: false 
      });
    },
  });
}